import type { Dispatch, SetStateAction } from "react";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { AuthUser } from "../../auth/shared/types/auth";
import type {
  ChallengeCategoryCode,
  ChallengeCategoryGender,
  ChallengeDetail,
} from "../../challenges/types/challengeAdmin";
import {
  categoryGenderLabel,
  categoryLabel,
} from "../../challenges/utils/challengeLabels";
import AdminChallengeCustomSettingsFields from "./AdminChallengeCustomSettingsFields";
import AdminChallengeScheduleFields from "./AdminChallengeScheduleFields";

type AdminChallengeGeneralFormProps = {
  categoryOptions: ChallengeCategoryCode[];
  challengeDetail: ChallengeDetail;
  challengeDurationDraft: string;
  challengeScheduledAtDraft: Date | null;
  challengeScheduledEndAtDraft: Date | null;
  genderOptions: ChallengeCategoryGender[];
  isAdmin: boolean;
  isTournamentChallenge: boolean;
  organizerUsers: AuthUser[];
  setChallengeDetail: Dispatch<SetStateAction<ChallengeDetail | null>>;
  setChallengeDurationDraft: Dispatch<SetStateAction<string>>;
  setChallengeScheduledAtDraft: Dispatch<SetStateAction<Date | null>>;
  setChallengeScheduledEndAtDraft: Dispatch<SetStateAction<Date | null>>;
  showLocationFields?: boolean;
  showScheduleFields?: boolean;
};

export default function AdminChallengeGeneralForm({
  categoryOptions,
  challengeDetail,
  challengeDurationDraft,
  challengeScheduledAtDraft,
  challengeScheduledEndAtDraft,
  genderOptions,
  isAdmin,
  isTournamentChallenge,
  organizerUsers,
  setChallengeDetail,
  setChallengeDurationDraft,
  setChallengeScheduledAtDraft,
  setChallengeScheduledEndAtDraft,
  showLocationFields = true,
  showScheduleFields = true,
}: AdminChallengeGeneralFormProps) {
  const t = siteTexts.admin;
  const createTexts = t.createChallengePage;
  const info = siteTexts.common.info;
  const challengeType = String(challengeDetail.challengeType || "")
    .trim()
    .toLowerCase();
  const usesMultipleCategories = ["challenge", "poco"].includes(challengeType);
  const categoryGender = challengeDetail.categoryGender || "mixed";
  return (
    <>
      {isTournamentChallenge && (
        <div className="admin-config-section-marker">
          <h4>{createTexts.generalInfoTitle}</h4>
          <p>{createTexts.generalInfoDescription}</p>
        </div>
      )}

      <label className="field">
        <span>{t.labels.challengeName}</span>
        <input
          type="text"
          value={challengeDetail.challengeName}
          onChange={(event) =>
            setChallengeDetail((prev) =>
              prev ? { ...prev, challengeName: event.target.value } : prev,
            )
          }
        />
      </label>

      <label className="field">
        <span>{t.labels.description}</span>
        <textarea
          rows={4}
          value={challengeDetail.description || ""}
          onChange={(event) =>
            setChallengeDetail((prev) =>
              prev ? { ...prev, description: event.target.value } : prev,
            )
          }
        />
      </label>

      {showLocationFields && (
        <>
          <div className="admin-row-two admin-form-block-location">
            <label className="field">
              <span>{createTexts.city}</span>
              <input
                type="text"
                value={challengeDetail.city || ""}
                onChange={(event) =>
                  setChallengeDetail((prev) =>
                    prev ? { ...prev, city: event.target.value } : prev,
                  )
                }
                placeholder={createTexts.eventCityPlaceholder}
                required
              />
            </label>
            <label className="field">
              <span>{createTexts.state}</span>
              <input
                type="text"
                value={challengeDetail.state || ""}
                onChange={(event) =>
                  setChallengeDetail((prev) =>
                    prev ? { ...prev, state: event.target.value } : prev,
                  )
                }
                placeholder="SC"
                maxLength={32}
              />
            </label>
          </div>

          <label className="field admin-form-block-address">
            <span>{createTexts.clubVenue}</span>
            <input
              type="text"
              value={challengeDetail.address || ""}
              onChange={(event) =>
                setChallengeDetail((prev) =>
                  prev ? { ...prev, address: event.target.value } : prev,
                )
              }
              placeholder={createTexts.eventClubVenuePlaceholder}
              required
            />
          </label>
        </>
      )}

      {!isTournamentChallenge && (
        <>
          <AdminChallengeCustomSettingsFields
            challengeType={challengeDetail.challengeType}
            value={challengeDetail}
            onChange={(patch) =>
              setChallengeDetail((prev) =>
                prev
                  ? {
                      ...prev,
                      ...patch,
                      gameCount:
                        patch.gameCount === undefined
                          ? prev.gameCount
                          : patch.gameCount === null ||
                              String(patch.gameCount).trim() === ""
                            ? null
                            : Math.floor(Number(patch.gameCount)),
                      gameDurationMinutes:
                        patch.gameDurationMinutes === undefined
                          ? prev.gameDurationMinutes
                          : patch.gameDurationMinutes === null ||
                              String(patch.gameDurationMinutes).trim() === ""
                            ? null
                            : Math.floor(Number(patch.gameDurationMinutes)),
                      courtsCount:
                        patch.courtsCount === undefined
                          ? prev.courtsCount
                          : patch.courtsCount === null ||
                              String(patch.courtsCount).trim() === ""
                            ? null
                            : Math.floor(Number(patch.courtsCount)),
                      playersCount:
                        patch.playersCount === undefined
                          ? prev.playersCount
                          : patch.playersCount === null ||
                              String(patch.playersCount).trim() === ""
                            ? null
                            : Math.floor(Number(patch.playersCount)),
                    }
                  : prev,
              )
            }
          />
          {!usesMultipleCategories && (
            <div className="admin-row-two admin-form-block-category">
              <label className="field">
                <span className="info-label">
                  {createTexts.gender}
                  <InfoTooltip content={info.openCategory} />
                </span>
                <select
                  value={categoryGender}
                  onChange={(event) => {
                    const nextGender = event.target
                      .value as ChallengeCategoryGender;
                    setChallengeDetail((prev) =>
                      prev ? { ...prev, categoryGender: nextGender } : prev,
                    );
                  }}
                >
                  {genderOptions.map((gender) => (
                    <option key={`edit-gender-${gender}`} value={gender}>
                      {categoryGenderLabel(gender)}
                    </option>
                  ))}
                </select>
              </label>

              {categoryGender === "mixed" || categoryGender === "open" ? (
                <>
                  <label className="field">
                    <span className="info-label">
                      {createTexts.maleCategory}
                      <InfoTooltip content={info.category} />
                    </span>
                    <select
                      value={challengeDetail.categoryMale || "6a"}
                      onChange={(event) => {
                        const nextCategory = event.target
                          .value as ChallengeCategoryCode;
                        setChallengeDetail((prev) =>
                          prev
                            ? {
                                ...prev,
                                categoryMale: nextCategory,
                              }
                            : prev,
                        );
                      }}
                    >
                      {categoryOptions.map((category) => (
                        <option
                          key={`edit-category-m-${category}`}
                          value={category}
                        >
                          {categoryLabel(category)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span className="info-label">
                      {createTexts.femaleCategory}
                      <InfoTooltip content={info.category} />
                    </span>
                    <select
                      value={challengeDetail.categoryFemale || "6a"}
                      onChange={(event) => {
                        const nextCategory = event.target
                          .value as ChallengeCategoryCode;
                        setChallengeDetail((prev) =>
                          prev
                            ? {
                                ...prev,
                                categoryFemale: nextCategory,
                              }
                            : prev,
                        );
                      }}
                    >
                      {categoryOptions.map((category) => (
                        <option
                          key={`edit-category-f-${category}`}
                          value={category}
                        >
                          {categoryLabel(category)}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              ) : (
                <label className="field">
                  <span className="info-label">
                    {createTexts.category}
                    <InfoTooltip content={info.category} />
                  </span>
                  <select
                    value={challengeDetail.category || "6a"}
                    onChange={(event) => {
                      const nextCategory = event.target
                        .value as ChallengeCategoryCode;
                      setChallengeDetail((prev) =>
                        prev ? { ...prev, category: nextCategory } : prev,
                      );
                    }}
                  >
                    {categoryOptions.map((category) => (
                      <option key={`edit-category-${category}`} value={category}>
                        {categoryLabel(category)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}
        </>
      )}

      {isAdmin && (
        <>
          <div
            className={`admin-row-two${
              isTournamentChallenge ? " admin-form-block-organization" : ""
            }`}
          >
            <label className="field">
              <span className="info-label">
                {createTexts.organization}
                <InfoTooltip content={info.userRole} />
              </span>
              <select
                value={
                  Boolean(challengeDetail.isOfficial ?? true)
                    ? "official"
                    : "organizer"
                }
                onChange={(event) => {
                  const next = event.target.value;
                  setChallengeDetail((prev) =>
                    prev
                      ? {
                          ...prev,
                          isOfficial: next === "official",
                          organizerUserId:
                            next === "official"
                              ? null
                              : (prev.organizerUserId ?? null),
                        }
                      : prev,
                  );
                }}
              >
                <option value="official">{createTexts.official}</option>
                <option value="organizer">{createTexts.organizer}</option>
              </select>
            </label>
            <label className="field">
              <span className="info-label">
                {createTexts.organizer}
                <InfoTooltip content={info.users} />
              </span>
              <select
                value={String(challengeDetail.organizerUserId ?? "")}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  setChallengeDetail((prev) =>
                    prev
                      ? {
                          ...prev,
                          organizerUserId: Number.isFinite(parsed)
                            ? Math.floor(parsed)
                            : null,
                        }
                      : prev,
                  );
                }}
                disabled={Boolean(challengeDetail.isOfficial ?? true)}
              >
                <option value="">
                  {Boolean(challengeDetail.isOfficial ?? true)
                    ? createTexts.emptyOption
                    : createTexts.select}
                </option>
                {organizerUsers.map((user) => (
                  <option key={`organizer-edit-${user.id}`} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

        </>
      )}

      {challengeType !== "tournament" && (
        <label className="field">
          <span className="info-label">
            {Boolean(challengeDetail.isOfficial ?? true)
              ? createTexts.includeRanking
              : createTexts.includeOrganizerRanking}
            <InfoTooltip
              content={
                Boolean(challengeDetail.isOfficial ?? true)
                  ? info.ranking
                  : info.organizerRanking
              }
            />
          </span>
          <select
            value={
              Boolean(challengeDetail.includeInRanking ?? true) ? "yes" : "no"
            }
            onChange={(event) => {
              const next = event.target.value === "yes";
              setChallengeDetail((prev) =>
                prev ? { ...prev, includeInRanking: next } : prev,
              );
            }}
          >
            <option value="yes">{createTexts.yes}</option>
            <option value="no">{createTexts.no}</option>
          </select>
        </label>
      )}

      {showScheduleFields && (
        <AdminChallengeScheduleFields
          challengeDetail={challengeDetail}
          challengeDurationDraft={challengeDurationDraft}
          challengeScheduledAtDraft={challengeScheduledAtDraft}
          challengeScheduledEndAtDraft={challengeScheduledEndAtDraft}
          isTournamentChallenge={isTournamentChallenge}
          setChallengeDetail={setChallengeDetail}
          setChallengeDurationDraft={setChallengeDurationDraft}
          setChallengeScheduledAtDraft={setChallengeScheduledAtDraft}
          setChallengeScheduledEndAtDraft={setChallengeScheduledEndAtDraft}
        />
      )}
    </>
  );
}
